import { useEffect, useState } from "react";
import { Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminGetDestinations, adminCreateDestination, adminUpdateDestination, adminDeleteDestination } from "./adminApi";
import { Text, Area, Toggle, ImageField, ListEditor, GalleryEditor, Modal, Label } from "./ui";
import { Header } from "./AdminTours";

const blank = {
  name: "", region: "", banner_image: "", overview: "", things_to_do: [], places_to_visit: [],
  best_time: "", gallery: [], map_embed: "", featured: false, seo: { meta_title: "", meta_description: "", keywords: "" },
};

export default function AdminDestinations() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => adminGetDestinations().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (editing.id) await adminUpdateDestination(editing.id, editing);
      else await adminCreateDestination(editing);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const del = async (d) => { if (!window.confirm(`Delete ${d.name}?`)) return; await adminDeleteDestination(d.id); toast.success("Deleted"); load(); };
  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const setSeo = (k, v) => setEditing((e) => ({ ...e, seo: { ...e.seo, [k]: v } }));

  const filtered = items.filter((d) => d.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <Header title="Destinations" count={items.length} onNew={() => setEditing({ ...blank })} q={q} setQ={setQ} newLabel="Add Destination" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="destinations-admin-grid">
        {filtered.map((d) => (
          <div key={d.id} className="bg-white border border-[#d4af37]/25 overflow-hidden">
            <div className="relative aspect-[3/2]"><img src={d.banner_image} alt="" className="w-full h-full object-cover" />{d.featured && <span className="absolute top-2 left-2 text-[10px] uppercase bg-[#d4af37] text-[#2d1616] px-2 py-0.5">Featured</span>}</div>
            <div className="p-4 flex items-center justify-between">
              <div><div className="font-serif text-lg text-[#3b1a1a]">{d.name}</div><div className="text-xs text-[#5a4b4b]">{d.region}</div></div>
              <div className="flex gap-1">
                <button onClick={() => setEditing({ ...blank, ...d, seo: { ...blank.seo, ...(d.seo || {}) } })} data-testid={`edit-dest-${d.id}`} className="w-8 h-8 grid place-items-center text-[#14523d] hover:bg-[#14523d] hover:text-white"><Edit className="w-4 h-4" /></button>
                <button onClick={() => del(d)} className="w-8 h-8 grid place-items-center text-[#7c1d1d] hover:bg-[#7c1d1d] hover:text-white"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? "Edit Destination" : "Add Destination"} wide>
        {editing && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Text label="Name *" value={editing.name} onChange={(v) => set("name", v)} />
              <Text label="Region" value={editing.region} onChange={(v) => set("region", v)} placeholder="e.g. Rajasthan" />
              <Text label="Best Time" value={editing.best_time} onChange={(v) => set("best_time", v)} />
              <Toggle label="Featured on homepage" value={editing.featured} onChange={(v) => set("featured", v)} />
            </div>
            <ImageField label="Banner Image" value={editing.banner_image} onChange={(v) => set("banner_image", v)} folder="destinations" />
            <Area label="Overview" value={editing.overview} onChange={(v) => set("overview", v)} rows={3} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ListEditor label="Things to Do" items={editing.things_to_do} onChange={(v) => set("things_to_do", v)} />
              <ListEditor label="Places to Visit" items={editing.places_to_visit} onChange={(v) => set("places_to_visit", v)} />
            </div>
            <GalleryEditor items={editing.gallery} onChange={(v) => set("gallery", v)} folder="destinations" />
            <Text label="Map Embed URL" value={editing.map_embed} onChange={(v) => set("map_embed", v)} />
            <div className="border-t border-[#d4af37]/25 pt-4"><Label>SEO</Label>
              <div className="grid gap-3 mt-1">
                <Text label="Meta Title" value={editing.seo.meta_title} onChange={(v) => setSeo("meta_title", v)} />
                <Area label="Meta Description" value={editing.seo.meta_description} onChange={(v) => setSeo("meta_description", v)} rows={2} />
              </div>
            </div>
            <div className="flex gap-3"><button onClick={save} disabled={saving} data-testid="save-dest" className="bg-[#7c1d1d] text-white px-8 py-3 text-xs tracking-[0.2em] uppercase disabled:opacity-60">{saving ? "Saving…" : "Save"}</button><button onClick={() => setEditing(null)} className="border border-[#d4af37]/40 px-8 py-3 text-xs tracking-[0.2em] uppercase">Cancel</button></div>
          </div>
        )}
      </Modal>
    </div>
  );
}
